import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import httpClient from "../../../../api/httpClient";
import Cookies from "js-cookie";
import { Package , MapPin, Users , DollarSign, Activity } from "lucide-react";

import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { ExpandableHeader } from "../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import OrderUtil from "../utils/OrderUtil";
import OrderActivity from "./OrderActivity";

const OrderDetailsView = ({ orderNumber, orderOfficeCode }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [isActivityLoading, setIsActivityLoading] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);

  const headerRef = useRef(null);
  const abortRef = useRef(null);

  const token = Cookies.get("authToken");

  const fetchOrder = useCallback(async () => {
    if (!orderNumber) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setErr(null);
    try {
      const res = await httpClient.get(
        `/api/orders/order-number/${encodeURIComponent(orderNumber)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Office-Code": orderOfficeCode,
            "Content-Type": "application/json",
          },
          signal: ctrl.signal,
        }
      );
      setOrder(res.data || {});
    } catch (e) {
      if (!axios.isCancel(e))
        setErr(e?.response?.data || e?.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [orderNumber, orderOfficeCode, token]);

  useEffect(() => {
    fetchOrder();
    return () => abortRef.current?.abort();
  }, [fetchOrder]);

  const activityOfficeCode = order?.officeCode || orderOfficeCode;

  const fetchActivityLogs = useCallback(async () => {
    if (!orderNumber || !activityOfficeCode) return;
    try {
      setIsActivityLoading(true);
      const res = await httpClient.get(
        `/api/orders/${encodeURIComponent(
          orderNumber
        )}/activity-logs/${encodeURIComponent(activityOfficeCode)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Office-Code": activityOfficeCode,
          },
        }
      );
      const sorted = Array.isArray(res.data)
        ? [...res.data].sort((a, b) => {
            const da = a?.updatedTime ? new Date(a.updatedTime).getTime() : 0;
            const db = b?.updatedTime ? new Date(b.updatedTime).getTime() : 0;
            return db - da; // newest first
          })
        : [];
      setActivityLogs(sorted);
    } catch (e) {
      console.error("Error fetching order activity logs:", e);
      setActivityLogs([]);
    } finally {
      setIsActivityLoading(false);
    }
  }, [orderNumber, activityOfficeCode, token]);

  // Fetch logs once the office code is known from the order response
  useEffect(() => {
    if (activityOfficeCode) fetchActivityLogs();
  }, [activityOfficeCode, fetchActivityLogs]);

  const openActivityPanel = () => {
    setShowActivityPanel(true);
    fetchActivityLogs(); // re-fetch so the timeline is fresh on open
  };

  // Same MM/DD/YYYY hh:mm AM/PM format the shipment timeline uses
  const formatDisplayDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const suffix = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // convert 0 to 12 for midnight
    const hourStr = String(hours).padStart(2, "0");

    return `${month}/${day}/${year} ${hourStr}:${minutes} ${suffix}`;
  };

      if (loading) {
        return (
          <div className="p-4 flex items-center justify-center h-full w-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600" />
          </div>
        );
      }

      if (err) {
        return (
          <div className="p-4 text-red-600">
            Failed to load order: {typeof err === "string" ? err : "Unknown error"}
          </div>
        );
      }

      if (!order) {
        return null;      // 👈 instead of showing “No order found”
      }


  // Prefer DTO fields; fall back to richer entity or XML-like names if present
  const headerInfo = {
    "Order Number": { value: order.orderNumber || "" },
    Origin: {
      value: order.origin || order?.pickup?.location?.locationName || "",
    },
    Destination: {
      value: order.destination || order?.dropoff?.location?.locationName || "",
    },
    Status: { value: order.status || "" },
  };

  const totalWeight = order.weight ?? OrderUtil.sumLineItemWeight(order) ?? null;

  const totalPallets = order.numberOfPallets ?? OrderUtil.sumPallets(order) ?? null;

  const pickupDate =
    order.pickupDate ??
    order?.pickup?.scheduledArrival?.date ??
    order?.pickup?.scheduledArrival?.Date ??
    null;

  const deliveryDate =
    order.deliveryDate ??
    order?.dropoff?.scheduledArrival?.date ??
    order?.dropoff?.scheduledArrival?.Date ??
    null;

  const items = order?.lineItems || order?.items || [];


  const receiver = order?.parties?.find((p) =>
    ["RECEIVER"].includes((p.type || "").toUpperCase())
  );
  const shipper = order?.parties?.find((p) =>
    ["SHIPPER"].includes((p.type || "").toUpperCase())
  );

  return (
     <div className={`w-full h-full min-h-0 flex flex-col transition-opacity duration-500`} >
      {/* Sticky header */}
      <div
        ref={headerRef}
        className="sticky top-0 z-10 bg-white shadow-md w-full"
      >
        <StickyHeader
          infoData={headerInfo}
          buttons={[]}
          mode={null}
          leftLabel="Order Details"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto  bg-white dark:bg-acs-dark-surface w-full p-fluid-1 scroll-container overscroll-contain text-gray-800 dark:text-acs-dark-text"

>
        {/* Order Details */}
       {/* === Order Details (Static Section) === */}
       <div className="p-fluid-2">
        <div className="mb-fluid-2 flex flex-wrap items-center justify-between gap-fluid-1">
          <h3 className="text-fluid-lg font-semibold">Order Details</h3>
          <button
            type="button"
            onClick={openActivityPanel}
            className="inline-flex items-center gap-2 rounded-md bg-acs-primary-navy px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-acs-primary-blue focus:outline-none focus:ring-2 focus:ring-acs-accent focus:ring-offset-2"
          >
            <Activity size={16} />
            Activity
          </button>
        </div>
         <div className="mb-fluid-3 overflow-hidden  border border-gray-300 dark:border-acs-dark-border  bg-white dark:bg-acs-dark-surface">
          <div className="
              bg-[#1e2a74] dark:bg-blue-900/40 
              text-white dark:acs-dark-text
              text-fluid-base font-medium 
              px-fluid-2 py-fluid-1.5
            ">
            Core Order Details
          </div>
            <div className="grid grid-cols-5 gap-x-fluid-2 gap-y-fluid-3 p-fluid-3 bg-[#F9FAFE] dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail label="Order Number" value={order.orderNumber} />
            <DisplayDetail
              label="Office Code"
              value={order.officeCode || orderOfficeCode}
            />
            <DisplayDetail label="PO Number" value={order.poNumber} />
            <DisplayDetail
              label="Requested Mode"
              value={order.requestedMode || order.movementType}
            />
            <DisplayDetail
              label="Created Date"
              value={OrderUtil.fmtDateTime24(order.createdDate)}
            />
            <DisplayDetail
              label="Updated Date"
              value={OrderUtil.fmtDateTime24(order.updatedDate)}
            />
            <DisplayDetail
              label="Payment Method"
              value={order.paymentMethod || "-"}
            />
            <DisplayDetail
              label="Order Value"
              value={
                order.orderTotalAmount != null
                  ? OrderUtil.fmtMoney(order.orderTotalAmount, order.currency)
                  : ""
              }
            />
          </div>
        </div>


        {/* Dates */}
        {/* <ExpandableHeader headerLabel="Dates">
          <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            <DisplayDetail label="Pickup From" value={fmtDateTime(pickupDate)} />
            <DisplayDetail label="Deliver By" value={fmtDateTime(deliveryDate)} />
          </div>
        </ExpandableHeader> */}
        <h3
          className="text-fluid-lg font-semibold mb-fluid-2"
        >
          Stops
        </h3>

        {/* Stops */}
        {(order?.stops ?? [])
          .slice() // don’t mutate original
          .sort((a, b) => (a.stopSequence ?? 0) - (b.stopSequence ?? 0))
          .map((s, idx) => {
            const typeLabel =
              s.stopType === "PICKUP"
                ? "Pick-Up"
                : s.stopType === "DROP_OFF" || s.stopType === "DROPOFF"
                ? "Drop-Off"
                : s.stopType || "Stop";

            const title = `Stop ${idx + 1}: ${typeLabel} – ${
              s.stopName || "—"
            }`;

            return (
              <ExpandableHeader key={s.id ?? idx} 
              headerLabel={title} 
              initiallyExpanded={false} 
              headerType="primary"
              icon={MapPin}>
                <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
                  <DisplayDetail label="Location Name" value={s.stopName} />
                  <DisplayDetail
                    label="Warehouse Name"
                    value={s.warehouseName}
                  />
                  <DisplayDetail label="Address 1" value={s.addressLine1} />
                  <DisplayDetail label="Address 2" value={s.addressLine2} />
                  <DisplayDetail label="City" value={s.city} />
                  <DisplayDetail label="State" value={s.state} />
                  <DisplayDetail label="Country" value={s.country} />
                  <DisplayDetail label="Postal" value={s.zipCode} />

                  {/* Dates (optional per stop) */}
                  <DisplayDetail
                    label="Scheduled Gate-In"
                    value={OrderUtil.fmtDateTime24(s.scheduledGateIn)}
                  />
                  <DisplayDetail
                    label="Scheduled Gate-Out"
                    value={OrderUtil.fmtDateTime24(s.scheduledGateOut)}
                  />
                  {/* Add these only if you store them */}
                  {/* <DisplayDetail label="Actual Gate-In"  value={fmtDateTime(s.actualGateIn)} /> */}
                  {/* <DisplayDetail label="Actual Gate-Out" value={fmtDateTime(s.actualGateOut)} /> */}
                </div>
              </ExpandableHeader>
            );
          })}

         <h3
          className="text-fluid-lg font-semibold mb-fluid-2"
        >
          Total
        </h3>

        {/* Totals */}
        <ExpandableHeader headerLabel="Totals"  icon={DollarSign} initiallyExpanded={false}>
          <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail
              label="Total Weight (LB)"
              value={
                totalWeight != null ? Number(totalWeight).toLocaleString() : ""
              }
            />
            <DisplayDetail
              label="Total Pallets"
              value={
                totalPallets != null
                  ? Number(totalPallets).toLocaleString()
                  : ""
              }
            />
          </div>
        </ExpandableHeader>

          {/* Line Items (read-only) */}
          <h3 className="text-fluid-lg font-semibold mb-fluid-2">Items</h3>

          {items.map((item, index) => {
            const itemEntries = [
              { label: "Line #", value: item.lineNumber },
              { label: "SKU", value: item.sku },
              { label: "Description", value: item.itemDescription },
              { label: "Item Quantity", value: item.itemQuantity },
              { label: "Quantity UOM", value: item.quantityUom },
              {
                label: "Weight Total",
                value:
                  item.weightTotal != null
                    ? `${Number(item.weightTotal).toLocaleString()} ${
                        item.weightUom || "LB"
                      }`
                    : "—",
              },
              { label: "Weight Qualifier", value: item.weightQualifier },
              {
                label: "Volume Total",
                value:
                  item.volumeTotal != null
                    ? `${Number(item.volumeTotal).toLocaleString()} ${
                        item.volumeUom || ""
                      }`
                    : "—",
              },
            ];

            return (
              <ExpandableHeader
                key={item.id ?? index}
                headerSection={`Item #${index + 1}`}
                headerLabel={item.sku || `Item ${index + 1}`}
                icon={Package}
                headerType="success"
                initiallyExpanded={false}
              >
                  <div className="grid grid-cols-5 gap-x-fluid-2 gap-y-fluid-3 
                        p-fluid-3 
                        bg-[#E0F2F1] dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">
                  {itemEntries.map((entry, i) => (
                    <DisplayDetail
                      key={i}
                      label={entry.label}
                      value={entry.value || "—"}
                    />
                  ))}

                  {/* Quantities breakdown */}
                  {Array.isArray(item.quantities) && item.quantities.length > 0 && (
                    <>
                      <div className="col-span-full mt-fluid-2 border-t border-gray-300 pt-fluid-2">
                        <h4 className="text-fluid-base font-semibold mb-fluid-1">
                          Quantities
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-fluid-2 gap-y-fluid-3">
                          {item.quantities.map((q, qIdx) => (
                            <React.Fragment key={qIdx}>
                              <DisplayDetail
                                label="Container Type"
                                value={q.containerType || "—"}
                              />
                              <DisplayDetail
                                label="Quantity"
                                value={
                                  q.quantity != null
                                    ? Number(q.quantity).toLocaleString()
                                    : "—"
                                }
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </ExpandableHeader>
            );
          })}



        <h3
          className="text-fluid-lg font-semibold mb-fluid-2"
        >
          Party
        </h3>

         {/* Parties */}
        <ExpandableHeader headerLabel="Parties" icon={Users} initiallyExpanded={false}>
          <div className="px-4 py-3 mb-2 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2 dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail
              label="Shipper"
              value={
                shipper?.name || "-"
              }
            />
            <DisplayDetail
              label="Receiver"
              value={
                receiver?.name || "-"
              }
            />
          </div>
        </ExpandableHeader>

      </div>
    </div>

      <OrderActivity
        isActivityLoading={isActivityLoading}
        activityLogs={activityLogs}
        formatDisplayDate={formatDisplayDate}
        isOpen={showActivityPanel}
        onClose={() => setShowActivityPanel(false)}
      />
    </div>
  );
};

export default OrderDetailsView;
